import Grid from '@material-ui/core/Grid'
import React, { useCallback } from 'react'
import { Button } from '../../../../components/common/Buttons'
import { ModalDialog } from '../../../../components/common/Dialogs'
import { useTrans } from '../../../../services/i18n'
import { useAlertContext } from '../../../../contexts/AlertContext'
import { useForm } from '../../../../hooks/useForm'
import { TextField } from '../../../../components/common/Inputs'
import * as Yup from 'yup'

const formOptions = {
  validateOnMount: false,
  validateOnChange: false,
  enableReinitialize: true,
}

// Schema validation
const useScheme = () => {
  const { trans } = useTrans()
  return Yup.object({
    score: Yup.number()
      .integer(trans('score-must-be-integer'))
      .min(0, trans('score-must-be-positive'))
      .max(100, trans('score-max-100'))
      .required(trans('required')),
  })
}

// Initial values
const getInitialValues = (form) => ({
  score: form?.score || 0,
})

export default function ScoreModal({
  form,
  open,
  onClose,
  updateScore,
  fetchList,
  batchUpdateStatus,
}) {
  const { trans } = useTrans()
  const schema = useScheme()
  const { successAlert, errorAlert } = useAlertContext()
  const [isSending, setIsSending] = React.useState(false)

  const closeModal = () => {
    resetForm()
    onClose()
  }

  const onSubmit = useCallback(
    async (values) => {
      if (!updateScore || !form) return

      setIsSending(true)
      try {
        const response = await updateScore({
          organizationFormId: form.id,
          score: values.score
        })

        if (response.error && response.error.message) {
          errorAlert(response.error.message)
        } else {
          // Update admin status to 'completed' after saving score
          await batchUpdateStatus({
            forms: [{
              organizationFormId: form.id,
              adminStatus: 'completed'
            }]
          })

          successAlert(response.payload?.message || trans('score-updated-successfully'))
          closeModal()
          fetchList()
        }
      } catch (error) {
        console.error('Error updating score:', error)
        errorAlert(trans('failed-to-update-score'))
      } finally {
        setIsSending(false)
      }
    },
    [updateScore, form, closeModal, fetchList, errorAlert, successAlert, trans, batchUpdateStatus]
  )

  const {
    getFieldProps,
    handleSubmit,
    values,
    isSubmitting,
    isValid,
    resetForm,
  } = useForm(schema, getInitialValues(form), onSubmit, formOptions)

  return (
    <ModalDialog
      open={open}
      onClose={closeModal}
      title={trans('assign-score')}
      actions={
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              disabled={isSending}
              onClick={closeModal}
            >
              {trans('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={isSending || !isValid}
              pending={isSending}
              onClick={handleSubmit}
            >
              {trans('confirm')}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            type="number"
            label={trans('score')}
            placeholder={trans('enter-score-0-100')}
            inputProps={{ min: 0, max: 100 }}
            {...getFieldProps('score')}
          />
        </Grid>
      </Grid>
    </ModalDialog>
  )
}
